import type { Item, Tag, Invite, CalendarImportSource, GoogleCalendarCandidate } from '@chaos-concierge/shared';
import type { CreateItemInput, DeviceReminder, ItemPatch, PlannerStore, SpaceMember, SpaceWithMembers } from './types';

const KEY = 'planner:items';
const LABELS_KEY = 'planner:labels';

function newId(): string {
  return String(Date.now()) + Math.random().toString(36).slice(2, 7);
}

function load(): Item[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Item[]) : [];
  } catch {
    return [];
  }
}

function save(items: Item[]): void {
  localStorage.setItem(KEY, JSON.stringify(items));
}

export class LocalStore implements PlannerStore {
  async list(): Promise<Item[]> {
    return load();
  }

  async create(input: CreateItemInput): Promise<Item> {
    const items = load();
    const now = Date.now();
    const item: Item = {
      id: newId(),
      ownerId: 'local',
      kind: input.kind,
      title: input.title,
      parentId: input.parentId,
      date: input.date,
      dueOffsetDays: input.kind === 'task' ? input.dueOffsetDays ?? null : null,
      time: (input.kind === 'event' || input.kind === 'task') ? input.time ?? null : null,
      endTime: input.kind === 'event' ? input.endTime ?? null : null,
      endDate: input.kind === 'event' ? input.endDate ?? null : null,
      important: input.important ?? false,
      needsAttention: input.kind === 'task' ? input.needsAttention ?? false : false,
      hidden: false,
      done: false,
      lastCompletedAt: null,
      completedCount: 0,
      completionHistory: [],
      priceHistory: [],
      reminder: null,
      recurrence: (input.kind === 'event' || input.kind === 'task') ? input.recurrence ?? null : null,
      recurrenceEnd: (input.kind === 'event' || input.kind === 'task') ? input.recurrenceEnd ?? null : null,
      occurrenceOverrides: {},
      listId: input.listId ?? null,
      sortOrder: null,
      location: input.kind === 'event' ? input.location ?? null : null,
      tagIds: [],
      spaceId: input.spaceId ?? null,
      createdAt: now,
      updatedAt: now,
    };
    save([...items, item]);
    return item;
  }

  async update(
    id: string,
    patch: ItemPatch,
  ): Promise<Item> {
    const items = load();
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) throw new Error(`Item ${id} not found`);
    const updated = { ...items[idx], ...patch, updatedAt: Date.now() };
    items[idx] = updated;
    save(items);
    return updated;
  }

  async remove(id: string): Promise<void> {
    const items = load();
    const kill = new Set<string>();
    const collect = (x: string) => {
      kill.add(x);
      items.filter((i) => i.parentId === x).forEach((c) => collect(c.id));
    };
    collect(id);
    save(items.filter((i) => !kill.has(i.id)));
  }

  async listTags(): Promise<Tag[]> {
    try { return JSON.parse(localStorage.getItem(LABELS_KEY) ?? '[]') as Tag[]; } catch { return []; }
  }

  async createTag(name: string, color: string): Promise<Tag> {
    const tags = await this.listTags();
    const tag: Tag = { id: newId(), ownerId: 'local', name, color, createdAt: Date.now() };
    localStorage.setItem(LABELS_KEY, JSON.stringify([...tags, tag]));
    return tag;
  }

  async updateTag(id: string, patch: Partial<Pick<Tag, 'name' | 'color'>>): Promise<Tag> {
    const tags = await this.listTags();
    const idx = tags.findIndex((t) => t.id === id);
    if (idx === -1) throw new Error(`Tag ${id} not found`);
    tags[idx] = { ...tags[idx], ...patch };
    localStorage.setItem(LABELS_KEY, JSON.stringify(tags));
    return tags[idx];
  }

  async removeTag(id: string): Promise<void> {
    const tags = await this.listTags();
    localStorage.setItem(LABELS_KEY, JSON.stringify(tags.filter((t) => t.id !== id)));
  }

  // ── shared spaces ───────────────────────────────────────────────────────────
  // Shared spaces require connectivity; the local fallback is personal-only.
  async listSpaces(): Promise<SpaceWithMembers[]> { return []; }
  async createSpace(): Promise<SpaceWithMembers> { throw new Error('Shared calendars require a connection'); }
  async deleteSpace(): Promise<void> { throw new Error('Shared calendars require a connection'); }
  async leaveSpace(): Promise<void> { throw new Error('Shared calendars require a connection'); }
  async inviteToSpace(): Promise<Invite> { throw new Error('Shared calendars require a connection'); }
  async revokeSpaceInvite(): Promise<void> { throw new Error('Shared calendars require a connection'); }
  async removeSpaceMember(): Promise<void> { throw new Error('Shared calendars require a connection'); }
  async updateSpaceMemberRole(): Promise<SpaceMember> { throw new Error('Shared calendars require a connection'); }
  async listInvites(): Promise<Invite[]> { return []; }
  async acceptInvite(): Promise<SpaceWithMembers> { throw new Error('Shared calendars require a connection'); }
  async declineInvite(): Promise<void> { throw new Error('Shared calendars require a connection'); }

  async listCalendarImports(): Promise<CalendarImportSource[]> { return []; }
  async startGoogleCalendarConnect(): Promise<{ authUrl: string }> { throw new Error('Google Calendar imports require a connection'); }
  async finishGoogleCalendarConnect(): Promise<{ accountEmail: string; calendars: GoogleCalendarCandidate[] }> { throw new Error('Google Calendar imports require a connection'); }
  async listGoogleCalendarCandidates(): Promise<GoogleCalendarCandidate[]> { return []; }
  async disconnectGoogleCalendarAccount(): Promise<void> { throw new Error('Google Calendar imports require a connection'); }
  async createGoogleCalendarImport(): Promise<CalendarImportSource> { throw new Error('Google Calendar imports require a connection'); }
  async updateCalendarImport(): Promise<CalendarImportSource> { throw new Error('Google Calendar imports require a connection'); }
  async syncCalendarImport(): Promise<CalendarImportSource> { throw new Error('Google Calendar imports require a connection'); }
  async removeCalendarImport(): Promise<void> { throw new Error('Google Calendar imports require a connection'); }

  async getPushConfig(): Promise<{ enabled: boolean; publicKey: string | null }> { return { enabled: false, publicKey: null }; }
  async savePushSubscription(): Promise<{ subscribed: boolean }> { throw new Error('Notifications require a connection'); }
  async removePushSubscription(): Promise<{ subscribed: boolean }> { return { subscribed: false }; }
  async listDeviceReminders(deviceId: string): Promise<DeviceReminder[]> {
    try { return JSON.parse(localStorage.getItem(`planner:device-reminders:${deviceId}`) ?? '[]') as DeviceReminder[]; } catch { return []; }
  }
  async setDeviceReminder(deviceId: string, itemId: string, reminder: string | null): Promise<DeviceReminder> {
    const reminders = await this.listDeviceReminders(deviceId);
    const next = reminders.filter((entry) => entry.itemId !== itemId);
    if (reminder) next.push({ itemId, reminder });
    localStorage.setItem(`planner:device-reminders:${deviceId}`, JSON.stringify(next));
    return { itemId, reminder };
  }
}
